import React, { useState } from 'react';
import { Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import { createCustomTextEntity } from 'views/App/Portal/Policy/DraftManuals/_data_sources/customTextEntities/manageCustomTextEntities';
import errorHelper from 'helpers/errorHelper';
import type {
  CreateCustomTextEntityRequest,
  CustomTextEntity,
} from 'views/App/Portal/Policy/_types/customTextEntities';
import type { ServerErrorResponse } from 'data_sources/constants';

interface AddCustomTextEntityModalProps {
  open: boolean;
  onClose: () => void;
  manualId: string;
  onSuccess: (newEntity: CustomTextEntity) => void;
}

export function AddCustomTextEntityModal({
  open,
  onClose,
  manualId,
  onSuccess,
}: AddCustomTextEntityModalProps): JSX.Element {
  const [isCreatingCustomEntity, setIsCreatingCustomEntity] = useState(false);
  const [customEntityForm, setCustomEntityForm] = useState<CreateCustomTextEntityRequest>({
    textEntity: '',
    substitutionValue: '',
    description: '',
  });
  const [customEntityErrors, setCustomEntityErrors] = useState<ServerErrorResponse | null>(null);

  const handleCancel = () => {
    setCustomEntityForm({
      textEntity: '',
      substitutionValue: '',
      description: '',
    });
    setCustomEntityErrors(null);
    onClose();
  };

  const handleSave = async () => {
    if (!manualId) return;

    // Validate required fields
    const trimmedTextEntity = customEntityForm.textEntity.trim().replace(/\s+/g, '');
    const trimmedSubstitutionValue = customEntityForm.substitutionValue.trim();

    if (!trimmedTextEntity || !trimmedSubstitutionValue) {
      return;
    }

    setIsCreatingCustomEntity(true);
    setCustomEntityErrors(null);

    try {
      // Add CUSTOM: prefix if not already present
      const textEntityWithPrefix = trimmedTextEntity.startsWith('CUSTOM:')
        ? trimmedTextEntity
        : `CUSTOM:${trimmedTextEntity}`;

      const formDataWithPrefix = {
        ...customEntityForm,
        textEntity: textEntityWithPrefix,
        substitutionValue: trimmedSubstitutionValue,
      };

      const newCustomEntity = await createCustomTextEntity(manualId, formDataWithPrefix);

      // Call the success callback
      onSuccess(newCustomEntity);

      // Close modal and reset form
      handleCancel();
    } catch (error: unknown) {
      if (typeof error === 'object' && error !== null && 'errorDetails' in error) {
        setCustomEntityErrors(error as ServerErrorResponse);
      } else {
        setCustomEntityErrors({
          errorMessage: 'An unexpected error occurred',
          errorDetails: [],
        } as ServerErrorResponse);
      }
    } finally {
      setIsCreatingCustomEntity(false);
    }
  };

  const handleFormChange = (field: keyof CreateCustomTextEntityRequest, value: string) => {
    setCustomEntityForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth='sm'
      fullWidth
    >
      <DialogTitle>Add Custom Text Entity</DialogTitle>
      <DialogContent>
        {customEntityErrors && errorHelper.renderAllErrors(customEntityErrors)}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label='Text Entity Name'
            value={customEntityForm.textEntity}
            onChange={(e) => handleFormChange('textEntity', e.target.value)}
            fullWidth
            required
          />
          <TextField
            label='Text Entity Value'
            value={customEntityForm.substitutionValue}
            onChange={(e) => handleFormChange('substitutionValue', e.target.value)}
            fullWidth
            required
          />
          <TextField
            label='Description'
            value={customEntityForm.description}
            onChange={(e) => handleFormChange('description', e.target.value)}
            fullWidth
            multiline
            rows={3}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          variant='outlined'
          color='neutral'
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant='contained'
          disabled={isCreatingCustomEntity || !customEntityForm.textEntity || !customEntityForm.substitutionValue}
        >
          {isCreatingCustomEntity ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
